import React, { useState, useRef } from 'react';
import { graphql } from 'gatsby';
import Image from 'gatsby-plugin-sanity-image';
import classNames from 'classnames';

import {
  getImageDimensions,
  getExtension,
  getImageAsset,
} from '@sanity/asset-utils';

import SanityPortableText from '../SanityPortableText';
import Pancake from './Pancake';
import Container from '../Container';
import PancakeIntro from '../PancakeIntro';
import Card from '../Card';
import Button from '../Button';

import './TextImage.scss';

const TextImage = (props) => {
  const {
    pancakeTitle,
    pancakeText,
    text,
    theme,
    image,
    video,
    imagePosition,
  } = props;

  const [videoPlaying, setVideoPlaying] = useState(false);

  const videoRef = useRef(null);

  const handlePlayVideo = () => {
    setVideoPlaying(true);
    videoRef.current.style.opacity = 1;
    videoRef.current.play();
  };

  const imageDimensions = image && getImageDimensions(image.image);
  const imageExtension = image && getExtension(image.image);
  const imageUrl =
    image &&
    getImageAsset(image.image, {
      projectId: process.env.GATSBY_SANITY_PROJECTID,
      dataset: process.env.GATSBY_SANITY_DATASET,
    }).url;

  return (
    <Pancake className="PancakeTextImage" theme={theme}>
      <Container>
        {(pancakeTitle || pancakeText) && (
          <PancakeIntro title={pancakeTitle} text={pancakeText} />
        )}
        <div
          className={classNames(
            'PancakeTextImageGrid',
            video && 'PancakeTextImageGridWithVideo'
          )}
        >
          {imagePosition === 'Right' ? (
            <>
              <div className="PancakeTextImageText">
                <SanityPortableText blocks={text} />
              </div>
              {video?.youtubeVideo && (
                <iframe
                  className="PancakeVideoYoutubePlayer"
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${video.youtubeVideo.videoID}`}
                  title="YouTube video player"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                ></iframe>
              )}

              {image && (
                <div className="baseImageContainer">
                  {image.callToAction && (
                    <div className="baseImageButtonContainer">
                      <Button
                        className="transparent teal no-padding"
                        arrow
                        to={image.callToAction.url}
                      >
                        {image.callToAction.label}
                      </Button>
                    </div>
                  )}
                  {imageExtension === 'svg' ? (
                    <img
                      src={imageUrl}
                      alt={image.altText || ''}
                      className="baseImage"
                      loading="lazy"
                      width={imageDimensions.width}
                      height={imageDimensions.height}
                    />
                  ) : (
                    <Image
                      {...image.image}
                      alt={image.altText || ''}
                      className="baseImage"
                      loading="lazy"
                      htmlWidth={imageDimensions.width}
                      htmlHeight={imageDimensions.height}
                    />
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {(video?.videoFile || video?.videoURL) && (
                <Card
                  theme="Light"
                  paddingSize="Half"
                  className="PancakeTextImageVideo"
                >
                  {!videoPlaying && (
                    <>
                      <button
                        onClick={handlePlayVideo}
                        aria-label="Play Video"
                        className="PancakeTextImageVideoPlayButton"
                      >
                        <svg
                          width="108"
                          height="108"
                          viewBox="0 0 108 108"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clipPath="url(#clip0_485_1560)">
                            <g opacity="0.45">
                              <path
                                opacity="0.45"
                                d="M53.5461 107.811C83.1188 107.811 107.092 83.6767 107.092 53.9055C107.092 24.1343 83.1188 0 53.5461 0C23.9734 0 0 24.1343 0 53.9055C0 83.6767 23.9734 107.811 53.5461 107.811Z"
                                fill="#00D1CA"
                              />
                            </g>
                            <path
                              d="M40.6133 53.9055V35.1946C40.6133 32.8804 43.1001 31.4321 45.0953 32.5892L61.1946 41.9483L77.2939 51.3073C79.2891 52.4645 79.2891 55.361 77.2939 56.5181L61.1946 65.8772L45.0953 75.2362C43.1001 76.3934 40.6133 74.9451 40.6133 72.6308V53.92V53.9055Z"
                              fill="#2D3748"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_485_1560">
                              <rect
                                width="107.085"
                                height="107.804"
                                fill="white"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </button>

                      {video?.videoThumbnail && (
                        <Image
                          {...video.videoThumbnail}
                          alt={''}
                          className="PancakeTextImageVideoCover"
                          loading="lazy"
                        />
                      )}
                    </>
                  )}
                  <video
                    width="320"
                    height="240"
                    controls
                    className="PancakeTextImageVideoPlayer"
                    ref={videoRef}
                  >
                    <source
                      src={video?.videoFile.asset.url || video.videoURL}
                      type="video/mp4"
                    />
                  </video>
                  <p className="PancakeTextImageVideoCaption">
                    Hear from companies building modern DevX
                  </p>
                </Card>
              )}
              {video?.youtubeVideo && (
                <iframe
                  className="PancakeVideoYoutubePlayer"
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${video.youtubeVideo.videoID}`}
                  title="YouTube video player"
                  frameborder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowfullscreen
                ></iframe>
              )}
              {image && (
                <div className="baseImageContainer">
                  {image.callToAction && (
                    <div className="baseImageButtonContainer">
                      <Button
                        className="corporate-light"
                        arrow
                        to={image.callToAction.url}
                      >
                        {image.callToAction.label}
                      </Button>
                    </div>
                  )}
                  {imageExtension === 'svg' ? (
                    <img
                      src={imageUrl}
                      alt={image.altText || ''}
                      className="baseImage"
                      loading="lazy"
                      width={imageDimensions.width}
                      height={imageDimensions.height}
                    />
                  ) : (
                    <Image
                      {...image.image}
                      alt={image.altText || ''}
                      className="baseImage"
                      loading="lazy"
                      htmlWidth={imageDimensions.width}
                      htmlHeight={imageDimensions.height}
                    />
                  )}
                </div>
              )}
              <div className="PancakeTextImageText">
                <SanityPortableText blocks={text} />
              </div>
            </>
          )}
        </div>
      </Container>
    </Pancake>
  );
};

export const query = graphql`
  fragment SanityPancakeTextImage on SanityTextImage {
    _key
    _type
    pancakeIntro {
      pancakeText
      pancakeTitle
    }
    _rawText(resolveReferences: { maxDepth: 1000 })
    image {
      altText
      callToAction {
        url
        label
      }
      image {
        ...ImageWithPreview
      }
    }
    video {
      videoURL
      videoCaption
      videoFile {
        asset {
          url
          extension
        }
      }
      videoThumbnail {
        ...ImageWithPreview
      }
      youtubeVideo {
        videoID
      }
    }
    theme
    imagePosition
  }
`;

export default TextImage;
